// frontend/src/pages/TopicInsightsPage.js
import React, { useState, useEffect } from 'react';
import { getTopicInsights } from '../services/api';
import ArticleCard from '../components/Dashboard/ArticleCard';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import {
    ChartBarIcon, TagIcon, BuildingStorefrontIcon, DocumentTextIcon,
    ArrowLeftIcon, ViewColumnsIcon // Removed XMarkIcon as it's not used in this version
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

// Helper to generate distinct colors for the bar chart (remains the same)
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#A2D2FF', '#FF8FAB', '#7B2CBF'];

// CustomTooltip component (remains the same)
const CustomTooltip = ({ active, payload, label, fullLabel }) => {
    if (active && payload && payload.length) {
        const displayLabel = fullLabel || label;
        return (
            <div className="bg-white p-3 shadow-lg rounded-md border border-gray-200">
                <p className="font-semibold text-gray-700">{displayLabel}</p>
                <p className="text-sm" style={{ color: payload[0].fill }}>
                    Mentions: {payload[0].value}
                </p>
            </div>
        );
    }
    return null;
};

// TopicPopularityChart component (remains the same)
const TopicPopularityChart = ({ topicsData, daysLookback }) => {
    if (!topicsData || topicsData.length === 0) {
        return (
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 mb-8 text-center">
                <h2 className="text-xl font-semibold text-gray-700 mb-4 flex items-center justify-center">
                    <ChartBarIcon className="w-6 h-6 mr-2 text-blue-600" />
                    Top Topic Mentions (Last {daysLookback} Days)
                </h2>
                <p className="text-gray-500">No data available to display chart.</p>
            </div>
        );
    }

    const chartData = topicsData.map(topic => ({
        name: topic.topic_name.length > 15 ? topic.topic_name.substring(0, 13) + '...' : topic.topic_name,
        full_name: topic.topic_name,
        count: topic.article_count_in_period,
    })).sort((a, b) => b.count - a.count);

    return (
        <div className="bg-white p-4 md:p-6 rounded-xl shadow-lg border border-gray-200 mb-10">
            <h2 className="text-xl font-semibold text-gray-700 mb-6 flex items-center">
                <ChartBarIcon className="w-6 h-6 mr-2 text-blue-600" />
                Top Topic Mentions (Last {daysLookback} Days)
            </h2>
            <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                    <BarChart
                        data={chartData}
                        margin={{ top: 5, right: 20, left: 0, bottom: 25 }}
                        barSize={25}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis
                            dataKey="name"
                            angle={-30}
                            textAnchor="end"
                            height={60}
                            interval={0}
                            tick={{ fontSize: 10, fill: '#6b7280' }}
                            tickFormatter={(value, index) => chartData[index]?.name || value}
                        />
                        <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                        <Tooltip
                            content={<CustomTooltip />}
                            cursor={{ fill: 'rgba(206, 212, 218, 0.3)' }}
                            formatter={(value, name, props) => [value, props.payload.full_name]}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                        <Bar dataKey="count" name="Article Mentions" radius={[4, 4, 0, 0]}>
                            {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

// TopicSourceBreakdownChart component (remains the same)
const TopicSourceBreakdownChart = ({ articles }) => {
    if (!articles || articles.length === 0) return null;
    const sourceCounts = articles.reduce((acc, article) => {
        const sourceName = article.source_name || "Unknown Source";
        acc[sourceName] = (acc[sourceName] || 0) + 1;
        return acc;
    }, {});
    const chartData = Object.entries(sourceCounts)
        .map(([name, count]) => ({ name: name.length > 15 ? name.substring(0, 13) + '...' : name, full_name: name, count }))
        .sort((a, b) => b.count - a.count).slice(0, 10);
    if (chartData.length === 0) return null;
    return (
        <div className="bg-gray-50 p-4 md:p-6 rounded-lg shadow-inner border border-gray-200 my-6">
            <h3 className="text-md font-semibold text-gray-700 mb-4 flex items-center">
                <BuildingStorefrontIcon className="w-5 h-5 mr-2 text-blue-500" />
                Article Count by Source (Top 10 from sample)
            </h3>
            <div style={{ width: '100%', height: 250 }}>
                <ResponsiveContainer>
                    <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 30, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: '#6b7280' }} />
                        <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 10, fill: '#6b7280' }} interval={0} tickFormatter={(value, index) => chartData[index]?.name || value} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(206, 212, 218, 0.3)' }} formatter={(value, name, props) => [value, props.payload.full_name]} />
                        <Bar dataKey="count" name="Articles" barSize={15} radius={[0, 4, 4, 0]}>
                            {chartData.map((entry, index) => (<Cell key={`cell-source-${index}`} fill={COLORS[(index + 2) % COLORS.length]} />))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

// TopicContentTypeBreakdownChart component (remains the same)
const TopicContentTypeBreakdownChart = ({ articles }) => {
    if (!articles || articles.length === 0) return null;
    const contentTypeCounts = articles.reduce((acc, article) => {
        const types = article.classification?.content_types || [];
        types.forEach(type => { if (type && typeof type === 'string') { acc[type] = (acc[type] || 0) + 1; } });
        return acc;
    }, {});
    const chartData = Object.entries(contentTypeCounts)
        .map(([name, count]) => ({ name: name.length > 15 ? name.substring(0, 13) + '...' : name, full_name: name, count }))
        .sort((a, b) => b.count - a.count).slice(0, 10);
    if (chartData.length === 0) return null;
    return (
        <div className="bg-gray-50 p-4 md:p-6 rounded-lg shadow-inner border border-gray-200 my-6">
            <h3 className="text-md font-semibold text-gray-700 mb-4 flex items-center">
                <DocumentTextIcon className="w-5 h-5 mr-2 text-green-500" />
                Article Count by Content Type (Top 10 from sample)
            </h3>
            <div style={{ width: '100%', height: 250 }}>
                <ResponsiveContainer>
                    <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 30, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: '#6b7280' }} />
                        <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 10, fill: '#6b7280' }} interval={0} tickFormatter={(value, index) => chartData[index]?.name || value} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(206, 212, 218, 0.3)' }} formatter={(value, name, props) => [value, props.payload.full_name]} />
                        <Bar dataKey="count" name="Articles" barSize={15} radius={[0, 4, 4, 0]}>
                            {chartData.map((entry, index) => (<Cell key={`cell-type-${index}`} fill={COLORS[(index + 4) % COLORS.length]} />))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};


const TopicInsightsPage = () => {
    const [insights, setInsights] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [actualDaysLookback, setActualDaysLookback] = useState(7);
    const [selectedTopicForFullView, setSelectedTopicForFullView] = useState(null);
    const [showFullArticleView, setShowFullArticleView] = useState(false);

    useEffect(() => {
        const fetchInsights = async () => {
            setIsLoading(true);
            setError('');
            try {
                const data = await getTopicInsights();
                if (data.error) {
                    setError(data.error);
                    setInsights(null); // Explicitly set to null on error
                } else {
                    const sortedTopicsData = data.topics_data ? [...data.topics_data].sort((a, b) => b.article_count_in_period - a.article_count_in_period) : [];
                    setInsights({ ...data, topics_data: sortedTopicsData });
                    if (data.parameters_used && typeof data.parameters_used.days_lookback === 'number') {
                        setActualDaysLookback(data.parameters_used.days_lookback);
                    }
                }
            } catch (err) {
                setError(err.message || "Failed to load topic insights.");
                setInsights(null); // Explicitly set to null on catch
            } finally {
                setIsLoading(false);
            }
        };
        fetchInsights();
    }, []);

    const handleSaveToggle = (articleId, isNowSaved) => {
        if (insights && insights.topics_data) {
            const updatedTopicsData = insights.topics_data.map(topicData => ({
                ...topicData,
                articles: topicData.articles.map(article =>
                    article.id === articleId ? { ...article, is_saved: isNowSaved } : article
                ),
            }));
            setInsights(prevInsights => ({ ...prevInsights, topics_data: updatedTopicsData }));
        }
        if (selectedTopicForFullView && selectedTopicForFullView.articles) {
            const updatedSelectedArticles = selectedTopicForFullView.articles.map(article =>
                article.id === articleId ? { ...article, is_saved: isNowSaved } : article
            );
            setSelectedTopicForFullView(prev => ({ ...prev, articles: updatedSelectedArticles }));
        }
    };
    
    const capitalizeEachWord = (str) => {
        if (!str) return '';
        return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    const handleViewAllArticles = (topicData) => {
        setSelectedTopicForFullView(topicData);
        setShowFullArticleView(true);
    };

    const handleCloseFullArticleView = () => {
        setShowFullArticleView(false);
        setSelectedTopicForFullView(null);
    };

    // Conditional Rendering Logic
    if (isLoading) {
        return (
            <div className="container mx-auto px-4 py-8 text-center">
                <LoadingSpinner />
                <p className="text-lg text-gray-600 mt-4">Loading Topic Insights...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto px-4 py-8 text-center">
                <p className="text-red-600 bg-red-100 p-4 rounded-md shadow">Error: {error}</p>
            </div>
        );
    }

    // This check MUST come after isLoading and error, and before trying to access insights.topics_data
    if (!insights || !insights.topics_data || insights.topics_data.length === 0) {
        return (
            <div className="container mx-auto px-4 py-8 text-center">
                <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 mb-8">
                    <h1 className="text-3xl font-bold text-gray-800 mb-6">Topic Insights</h1>
                    {/* Pass empty array to chart if insights.topics_data is not available yet or empty */}
                    <TopicPopularityChart topicsData={[]} daysLookback={actualDaysLookback} />
                    <p className="text-gray-500 text-lg mt-4">No topic insights available at the moment.</p>
                </div>
            </div>
        );
    }

    // Main rendering logic for when insights and insights.topics_data ARE available
    if (showFullArticleView && selectedTopicForFullView) {
        // Render Two-Pane View
        return (
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
                <button
                    onClick={handleCloseFullArticleView}
                    className="absolute top-8 left-4 sm:left-6 lg:left-8 mb-6 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 z-10"
                >
                    <ArrowLeftIcon className="w-5 h-5 mr-2 text-gray-500" />
                    Back to Insights
                </button>

                <div className="pt-20 flex flex-col lg:flex-row gap-8">
                    <div className="lg:w-2/5 space-y-6">
                        <section className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
                            <div className="flex flex-col sm:flex-row sm:items-center mb-4 pb-3 border-b border-gray-200">
                                <TagIcon className="w-7 h-7 text-blue-600 mr-3 mb-2 sm:mb-0" />
                                <h2 className="text-xl font-semibold text-gray-800 capitalize">
                                    {capitalizeEachWord(selectedTopicForFullView.topic_name)}
                                </h2>
                            </div>
                            <p className="text-sm text-gray-600 mb-4">
                                Total mentions in last {actualDaysLookback} days: <span className="font-bold">{selectedTopicForFullView.article_count_in_period}</span>
                            </p>
                            <TopicSourceBreakdownChart articles={selectedTopicForFullView.articles} />
                            <TopicContentTypeBreakdownChart articles={selectedTopicForFullView.articles} />
                        </section>
                    </div>

                    <div className="lg:w-3/5">
                        <h3 className="text-xl font-semibold text-gray-800 mb-6">
                            All Articles for "{capitalizeEachWord(selectedTopicForFullView.topic_name)}"
                        </h3>
                        {selectedTopicForFullView.articles && selectedTopicForFullView.articles.length > 0 ? (
                            <div className="max-h-[calc(100vh-200px)] overflow-y-auto pr-2 space-y-6 custom-scrollbar">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {selectedTopicForFullView.articles.map(article => (
                                        <ArticleCard
                                            key={article.id}
                                            article={article}
                                            onSaveToggle={handleSaveToggle}
                                        />
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <p className="text-gray-500">No articles to display for this topic.</p>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Default View: List of Topics
    return (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-10 text-center sm:text-left">Topic Insights</h1>
            {/* This is safe because we've already checked insights and insights.topics_data above */}
            <TopicPopularityChart topicsData={insights.topics_data} daysLookback={actualDaysLookback} />

            {insights.topics_data.map((topicData, index) => (
                <section key={index} className="mb-16 bg-white p-6 rounded-xl shadow-lg border border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:items-center mb-6 pb-4 border-b-2 border-blue-500">
                        <TagIcon className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600 mr-3 mb-2 sm:mb-0" />
                        <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 capitalize">
                            {capitalizeEachWord(topicData.topic_name)}
                        </h2>
                        <span className="ml-0 sm:ml-auto mt-2 sm:mt-0 text-xs sm:text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                            {topicData.article_count_in_period} mentions in last {actualDaysLookback} days
                        </span>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        <TopicSourceBreakdownChart articles={topicData.articles} />
                        <TopicContentTypeBreakdownChart articles={topicData.articles} />
                    </div>
                    
                    <div className="mt-6 text-center">
                        <button
                            onClick={() => handleViewAllArticles(topicData)}
                            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <ViewColumnsIcon className="w-5 h-5 mr-2" />
                            View All {topicData.articles.length} Articles & Details 
                        </button>
                         <p className="text-xs text-gray-500 mt-2">
                            (Note: Charts above are based on this sample of {topicData.articles.length} articles)
                        </p>
                    </div>
                </section>
            ))}
        </div>
    );
};

export default TopicInsightsPage;
